import type { LessonConnectionLog } from './interfaces'
import type { LessonConnectionLogEntry, LessonConnectionSummary } from './lesson-connection-log'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { resolveUrl } from '@shared/lib/url'
import { useDayjs } from '@shared/lib/format'
import { AppRoute } from '@shared/config'

import {
  buildConnectionLogEntries,
  buildConnectionSummaries,
} from './lesson-connection-log'
import { useLessonConnectionQualityApi } from '../../api/lessons/connection-quality.hook'

interface LessonConnectionLogReturn {
  refIsLoading: Ref<boolean>
  refEntries: ComputedRef<LessonConnectionLogEntry[]>
  refSummaries: ComputedRef<LessonConnectionSummary[]>
  refIsEmpty: ComputedRef<boolean>
  refLessonsUrl: string
  formatTime: (value: string) => string
}

export const useLessonConnectionLog = (): LessonConnectionLogReturn => {
  const route = useRoute()
  const dayjs = useDayjs()

  const refLessonsUrl = resolveUrl(AppRoute.DisciplinesLessons, { id: route.params.id as string }) ?? ''

  const refIsLoading = ref<boolean>(false)
  const refLogs = ref<LessonConnectionLog[]>([])

  const refEntries = computed<LessonConnectionLogEntry[]>(() => buildConnectionLogEntries(refLogs.value))

  const refSummaries = computed<LessonConnectionSummary[]>(() => buildConnectionSummaries(refLogs.value))

  const refIsEmpty = computed<boolean>(() => !refIsLoading.value && refLogs.value.length === 0)

  const formatTime = (value: string): string => dayjs(value, DateFormat.Default).format(DateFormat.ExtendedTime)

  const load = async (): Promise<void> => {
    refIsLoading.value = true

    const logs = await useLessonConnectionQualityApi({
      disciplineId: route.params.id as string,
      lessonId: route.params.lessonId as string,
    })

    refLogs.value = logs ?? []
    refIsLoading.value = false
  }

  onMounted(load)

  return {
    refIsLoading,
    refEntries,
    refSummaries,
    refIsEmpty,
    refLessonsUrl,
    formatTime,
  }
}
